import { useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowLeft,
  CheckCircle,
  ChevronDown,
  ArrowRight,
  Phone,
  Mail,
} from "lucide-react";
import SectionHeading from "../components/SectionHeading";
import IntakeModal from "../components/IntakeModal";
import InsuranceInquiryModal from "../components/InsuranceInquiryModal";
import EducationInquiryModal from "../components/EducationInquiryModal";
import { DIVISIONS } from "../lib/divisions";

/**
 * SubServicePage
 *
 * A single reusable layout for:
 *  - Veteran service sub-pages (/veteran-services/:slug)
 *  - Insurance product sub-pages (/insurance/:slug)
 *  - Education program sub-pages (/education/:slug)
 */
const SubServicePage = ({ dataMap, division }) => {
  const { slug } = useParams();
  const [showModal, setShowModal] = useState(false);
  const service = dataMap[slug];

  if (!service) {
    const fallback = {
      veteran: "/veteran-services",
      insurance: "/insurance",
      education: "/education",
    }[division] || "/";
    return <Navigate to={fallback} replace />;
  }

  const isInsurance = division === "insurance";
  const isEducation = division === "education";
  const divisionInfo =
    (isInsurance && DIVISIONS.insurance) ||
    (isEducation && DIVISIONS.education) ||
    DIVISIONS.veteran;

  const ctaLabel = isInsurance
    ? "Request Consultation"
    : isEducation
    ? "Request Guidance"
    : "Start Intake Form";

  return (
    <div className="pt-24">
      {/* Hero */}
      <section className="relative bg-[#0B2343] text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]">
          <img
            src="https://images.unsplash.com/photo-1676286155307-9fd53cd4b431?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHwxfHxhbWVyaWNhbiUyMGZsYWclMjB0ZXh0dXJlJTIwY2xvc2UlMjB1cHxlbnwwfHx8fDE3Njk4Njg1NDZ8MA&ixlib=rb-4.1.0&q=85"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="max-w-5xl mx-auto px-6 md:px-8 py-16 md:py-24 relative z-10">
          <Link
            to={service.parentSlug}
            className="inline-flex items-center gap-2 text-[#C89B3C] hover:text-white text-sm font-medium mb-6 transition-colors"
            data-testid="sub-back-link"
          >
            <ArrowLeft size={16} /> Back to {service.parentLabel}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="h-[3px] w-14 bg-[#C89B3C] mb-6" />
            <span className="text-xs font-medium tracking-[0.2em] uppercase text-[#C89B3C] mb-3 block">
              {service.eyebrow}
            </span>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-6">
              {service.title}
            </h1>
            <p className="text-lg md:text-xl text-slate-300 leading-relaxed max-w-3xl">
              {service.tagline}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => setShowModal(true)}
                className="bg-[#C89B3C] text-white px-8 py-4 font-medium hover:bg-[#a8852f] transition-all inline-flex items-center justify-center gap-2"
                data-testid="sub-primary-cta"
              >
                {ctaLabel}
                <ArrowRight size={18} />
              </button>
              <a
                href={divisionInfo.phoneHref}
                className="border-2 border-white text-white hover:bg-white hover:text-[#0B2343] px-8 py-4 font-medium transition-all inline-flex items-center justify-center gap-2"
                data-testid="sub-phone-cta"
              >
                <Phone size={18} />
                {divisionInfo.phone}
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Overview */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 md:px-8">
          <SectionHeading eyebrow="Overview" title="What this looks like." />
          <p className="text-lg text-slate-700 leading-relaxed">{service.intro}</p>
        </div>
      </section>

      {/* Services or Benefits list */}
      {service.services && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6 md:px-8">
            <SectionHeading
              eyebrow="What's included"
              title="How we support this work."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {service.services.map((s, idx) => (
                <motion.div
                  key={s.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{ duration: 0.5, delay: idx * 0.06 }}
                  className="bg-white p-6 border border-slate-200 hover:border-[#C89B3C] transition-colors"
                >
                  <div className="flex items-start gap-3">
                    <CheckCircle className="text-[#C89B3C] flex-shrink-0 mt-1" size={18} />
                    <div>
                      <h3 className="font-serif text-lg font-bold text-slate-900 mb-1">
                        {s.title}
                      </h3>
                      <p className="text-slate-600 text-sm leading-relaxed">{s.desc}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Who it's for + Benefits (insurance) */}
      {service.whoFor && service.benefits && (
        <section className="py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-6 md:px-8 grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white p-8 border border-slate-200">
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">Who it's for</h3>
              <ul className="space-y-3">
                {service.whoFor.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-slate-700">
                    <CheckCircle className="text-[#C89B3C] flex-shrink-0 mt-1" size={16} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white p-8 border border-slate-200">
              <h3 className="font-serif text-2xl font-bold text-slate-900 mb-4">Benefits</h3>
              <ul className="space-y-3">
                {service.benefits.map((item) => (
                  <li key={item} className="flex items-start gap-2 text-slate-700">
                    <CheckCircle className="text-[#C89B3C] flex-shrink-0 mt-1" size={16} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {service.faqs && service.faqs.length > 0 && (
        <section className="py-20 bg-white">
          <div className="max-w-3xl mx-auto px-6 md:px-8">
            <SectionHeading eyebrow="FAQ" title="Common questions." align="center" />
            <div className="divide-y divide-slate-200 border-t border-b border-slate-200">
              {service.faqs.map((f, idx) => (
                <FaqItem key={f.q} q={f.q} a={f.a} testId={`sub-faq-${idx}`} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Notice for veteran services */}
      {division === "veteran" && (
        <section className="py-12 bg-amber-50 border-y border-amber-200">
          <div className="max-w-3xl mx-auto px-6 md:px-8">
            <p className="text-slate-700 leading-relaxed text-sm">
              <strong>Important:</strong> Noble Strategies Consulting does not make guarantees
              regarding VA decisions or disability ratings. We are not a law firm, healthcare
              provider, or accredited VA representative. Our services are consultative and
              educational.
            </p>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-3xl mx-auto px-6 md:px-8 text-center">
          <div className="h-[3px] w-14 bg-[#C89B3C] mx-auto mb-6" />
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {isInsurance
              ? "Ready for a personalized quote?"
              : isEducation
              ? "Ready to plan your next step?"
              : "Ready to move forward?"}
          </h2>
          <p className="text-slate-600 mb-8">
            {isInsurance
              ? "Share a few details and we'll follow up within 24 hours — no obligation."
              : isEducation
              ? "Tell us your goal and we'll map out how to get there."
              : "Complete our intake form and a consultant will follow up within 24–48 hours."}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
            <button
              onClick={() => setShowModal(true)}
              className="bg-[#0B2343] text-white px-8 py-4 font-medium hover:bg-slate-800 transition-colors"
              data-testid="sub-cta-modal"
            >
              {ctaLabel}
            </button>
            <a
              href={`mailto:${divisionInfo.email}`}
              className="border-2 border-[#0B2343] text-[#0B2343] hover:bg-[#0B2343] hover:text-white px-8 py-4 font-medium transition-all inline-flex items-center justify-center gap-2"
            >
              <Mail size={18} />
              {divisionInfo.email}
            </a>
          </div>
        </div>
      </section>

      {isInsurance ? (
        <InsuranceInquiryModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          defaultService={service.title}
        />
      ) : isEducation ? (
        <EducationInquiryModal
          isOpen={showModal}
          onClose={() => setShowModal(false)}
          defaultProgram={service.title}
        />
      ) : (
        <IntakeModal isOpen={showModal} onClose={() => setShowModal(false)} />
      )}
    </div>
  );
};

const FaqItem = ({ q, a, testId }) => {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between py-5 text-left"
        data-testid={testId}
      >
        <span className="font-serif text-lg font-semibold text-slate-900 pr-4">{q}</span>
        <ChevronDown
          size={22}
          className={`text-[#C89B3C] flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && <div className="pb-5 text-slate-600 leading-relaxed">{a}</div>}
    </div>
  );
};

export default SubServicePage;
